import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { emergenciasAPI } from '../../services/api'

// Módulo Emergencias (partes de servicio). Comparte la fuente con el Cuadro de Listas:
// cada registro es un CuadroEvento tipo='emergencia'. No hay sincronización: es el mismo dato.

const initialState = {
  items: [],
  periodo: null, // { anio, mes } del último fetch — para que consumidores (badge del calendario) no usen otro mes
  detalle: null,
  loading: false,
  saving: false,
  error: null,
}

export const fetchEmergencias = createAsyncThunk(
  'emergencias/fetch',
  async (params = {}, { rejectWithValue }) => {
    try {
      const r = await emergenciasAPI.list(params)
      return r.data?.data || []
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e?.message || 'No fue posible cargar las emergencias')
    }
  }
)

export const fetchEmergencia = createAsyncThunk(
  'emergencias/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const r = await emergenciasAPI.get(id)
      return r.data?.data
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e?.message || 'No fue posible cargar la emergencia')
    }
  }
)

export const createEmergencia = createAsyncThunk(
  'emergencias/create',
  async (data, { rejectWithValue }) => {
    try {
      const r = await emergenciasAPI.create(data)
      return r.data?.data
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e?.message || 'No fue posible crear la emergencia')
    }
  }
)

export const updateEmergencia = createAsyncThunk(
  'emergencias/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const r = await emergenciasAPI.update(id, data)
      return r.data?.data
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e?.message || 'No fue posible actualizar la emergencia')
    }
  }
)

export const deleteEmergencia = createAsyncThunk(
  'emergencias/delete',
  async (id, { rejectWithValue }) => {
    try {
      await emergenciasAPI.remove(id)
      return id
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e?.message || 'No fue posible eliminar la emergencia')
    }
  }
)

const slice = createSlice({
  name: 'emergencias',
  initialState,
  reducers: {
    clearEmergenciasState() {
      return initialState
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchEmergencias.pending, (s) => { s.loading = true; s.error = null })
      .addCase(fetchEmergencias.fulfilled, (s, a) => { s.loading = false; s.items = a.payload; s.periodo = a.meta?.arg || null })
      .addCase(fetchEmergencias.rejected, (s, a) => { s.loading = false; s.error = a.payload })
      .addCase(fetchEmergencia.fulfilled, (s, a) => { s.detalle = a.payload })
      .addCase(createEmergencia.pending, (s) => { s.saving = true; s.error = null })
      .addCase(createEmergencia.fulfilled, (s, a) => {
        s.saving = false
        if (!a.payload) return
        // items está filtrado por el período (anio/mes). El form permite CUALQUIER fecha → sólo
        // insertar si la emergencia cae en el período visible (fecha canon = mediodía UTC).
        const p = s.periodo
        if (p && a.payload.fecha) {
          const d = new Date(a.payload.fecha)
          if (d.getUTCFullYear() !== Number(p.anio) || (d.getUTCMonth() + 1) !== Number(p.mes)) return
        }
        s.items.unshift(a.payload)
      })
      .addCase(createEmergencia.rejected, (s, a) => { s.saving = false; s.error = a.payload })
      .addCase(updateEmergencia.pending, (s) => { s.saving = true; s.error = null })
      .addCase(updateEmergencia.fulfilled, (s, a) => {
        s.saving = false
        if (!a.payload) return
        const i = s.items.findIndex((x) => x.id === a.payload.id)
        if (i >= 0) s.items[i] = { ...s.items[i], ...a.payload }
        if (s.detalle?.id === a.payload.id) s.detalle = { ...s.detalle, ...a.payload }
      })
      .addCase(updateEmergencia.rejected, (s, a) => { s.saving = false; s.error = a.payload })
      .addCase(deleteEmergencia.fulfilled, (s, a) => { s.items = s.items.filter((x) => x.id !== a.payload) })
  },
})

export const { clearEmergenciasState } = slice.actions
export default slice.reducer
